import React, { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import { productImages } from "../Data/ProductData";
import { IoClose, IoChevronBack, IoChevronForward } from "react-icons/io5";
import { motion, AnimatePresence } from "framer-motion";
import PageBanner from "./Pagebanner";

const ProductList = () => {
  const { category } = useParams();
  const categories = Object.keys(productImages);

  const defaultCategory =
    category && categories.includes(category) ? category : categories[0];

  const [activeTab, setActiveTab] = useState(defaultCategory);
  const [showModal, setShowModal] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [isLoading, setIsLoading] = useState(false);

  const openModal = (index) => {
    setSelectedIndex(index);
    setShowModal(true);
  };

  const closeModal = () => setShowModal(false);

  const changeCategory = (newCategory) => {
    setIsLoading(true);
    setTimeout(() => {
      setActiveTab(newCategory);
      setIsLoading(false);
    }, 300);
  };

  const nextImage = () => {
    setSelectedIndex((selectedIndex + 1) % productImages[activeTab].length);
  };

  const prevImage = () => {
    setSelectedIndex(
      (selectedIndex - 1 + productImages[activeTab].length) %
      productImages[activeTab].length
    );
  };

  // Utility function to clean and format product names
  const formatProductName = (filename) => {
    return filename
      .replace(/\.(jpeg|jpg|png|jfif|webp|gif)$/i, '') // Remove file extensions
      .replace(/[-_]/g, ' ') // Replace hyphens and underscores with spaces
      .toLowerCase() // Convert to lowercase first
      .replace(/\b\w/g, (char) => char.toUpperCase()) // Capitalize first letter of each word (Title Case)
      .replace(/\s+/g, ' ') // Replace multiple spaces with single space
      .trim(); // Remove leading/trailing spaces
  };

  // Enhanced category names
  const categoryNames = {
    breakers: "Breakers & Contactors",
    cables: "Cables & Hose",
    accessories: "Electrical Accessories",
    fans: "Exhaust & Ventilation Fans",
    explosionProof: "Explosion Proof",
    led: "LED Solutions",
    nonLed: "Non LED",
    switches: "Switches & Sockets",
    hardware: "Hardware Items"
  };

  // Animation variants
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.05,
        delayChildren: 0.1
      }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20, scale: 0.9 },
    visible: {
      opacity: 1,
      y: 0,
      scale: 1,
      transition: {
        duration: 0.4,
        ease: "easeOut"
      }
    }
  };

  const tabVariants = {
    inactive: { scale: 1, y: 0 },
    active: { scale: 1.05, y: -2 }
  };

  return (
    <div className="relative min-h-screen bg-gradient-to-br from-gray-50 via-white to-gray-100 overflow-hidden">
      {/* Animated Background Elements */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <motion.div
          className="absolute -top-40 -left-40 w-80 h-80 bg-gradient-to-br from-primary/5 to-secondary/5 rounded-full blur-3xl"
          animate={{
            scale: [1, 1.2, 1],
            rotate: [0, 180, 360]
          }}
          transition={{
            duration: 20,
            repeat: Infinity,
            ease: "linear"
          }}
        />
        <motion.div
          className="absolute top-1/3 -right-32 w-64 h-64 bg-gradient-to-br from-blue-400/10 to-purple-400/10 rounded-full blur-2xl"
          animate={{
            scale: [1.2, 1, 1.2],
            rotate: [360, 180, 0]
          }}
          transition={{
            duration: 25,
            repeat: Infinity,
            ease: "linear"
          }}
        />
      </div>

      <div className="relative z-10 max-w-7xl mx-auto px-4 md:px-8 py-12">
        {/* Header Section */}
        <motion.div
          initial={{ opacity: 0, y: -30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          className="text-center mb-16"
        >
          <motion.h1
            className="text-4xl md:text-6xl font-black text-primary mb-4"
            animate={{
              backgroundPosition: ["0% 50%", "100% 50%", "0% 50%"]
            }}
            transition={{
              duration: 8,
              repeat: Infinity,
              ease: "easeInOut"
            }}
          >
            Product <span className="text-secondary">Gallery</span>
          </motion.h1>
          <p className="text-xl text-gray-600 max-w-2xl mx-auto">
            Explore our comprehensive range of industrial solutions
          </p>
        </motion.div>

        {/* Product Count */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="flex justify-center mb-12"
        >
          <motion.div
            className="bg-secondary/20 px-6 py-3 rounded-full"
            animate={{ scale: [1, 1.02, 1] }}
            transition={{ duration: 2, repeat: Infinity }}
          >
            <span className="text-primary font-bold text-lg">
              {productImages[activeTab]?.length || 0} Products
            </span>
          </motion.div>
        </motion.div>

        {/* Enhanced Category Tabs */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
          className="mb-16"
        >
          <div className="flex flex-wrap gap-4 justify-center">
            {categories.map((cat, index) => (
              <motion.button
                key={cat}
                variants={tabVariants}
                animate={activeTab === cat ? "active" : "inactive"}
                whileHover={{ scale: 1.05, y: -2 }}
                whileTap={{ scale: 0.95 }}
                onClick={() => changeCategory(cat)}
                className={`group relative px-8 py-4 rounded-2xl font-bold transition-all duration-300 border-2 shadow-lg backdrop-blur-sm overflow-hidden ${activeTab === cat
                  ? "bg-gradient-to-r from-primary to-primary/90 text-white border-primary shadow-2xl"
                  : "bg-white/80 text-gray-700 hover:text-primary border-gray-200 hover:border-primary/30 hover:bg-white hover:shadow-xl"
                  }`}
              >
                {/* Animated background for active tab */}
                {activeTab === cat && (
                  <motion.div
                    className="absolute inset-0 bg-gradient-to-r from-secondary/20 to-transparent"
                    animate={{ x: ["-100%", "100%"] }}
                    transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
                  />
                )}

                <span className="relative z-10 text-sm md:text-base">
                  {categoryNames[cat] || cat}
                </span>

                {/* Active indicator */}
                {activeTab === cat && (
                  <motion.div
                    className="absolute bottom-0 left-1/2 w-8 h-1 bg-secondary rounded-full"
                    initial={{ scale: 0, x: "-50%" }}
                    animate={{ scale: 1, x: "-50%" }}
                    transition={{ delay: 0.2 }}
                  />
                )}
              </motion.button>
            ))}
          </div>
        </motion.div>

        {/* Loading State */}
        <AnimatePresence>
          {isLoading && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="flex justify-center items-center py-20"
            >
              <motion.div
                className="w-16 h-16 border-4 border-primary/20 border-t-primary rounded-full"
                animate={{ rotate: 360 }}
                transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
              />
            </motion.div>
          )}
        </AnimatePresence>

        {/* Product Grid - Pure Grid Layout */}
        <AnimatePresence mode="wait">
          {!isLoading && (
            <motion.div
              key={activeTab}
              variants={containerVariants}
              initial="hidden"
              animate="visible"
              exit="hidden"
              className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-4 md:gap-6"
            >
              {productImages[activeTab]?.map((img, i) => (
                <motion.div
                  key={`${activeTab}-${i}`}
                  variants={itemVariants}
                  whileHover={{
                    y: -8,
                    scale: 1.03,
                    rotateY: 5
                  }}
                  whileTap={{ scale: 0.98 }}
                  onClick={() => openModal(i)}
                  className="group cursor-pointer bg-white rounded-2xl border border-gray-200 shadow-lg hover:shadow-2xl transition-all duration-500 overflow-hidden"
                >
                  {/* Image Container */}
                  <div className="relative w-full aspect-square overflow-hidden">
                    <div className="w-full h-full bg-gradient-to-br from-gray-50 to-gray-100 flex items-center justify-center p-4">
                      <motion.img
                        src={`/${activeTab}/${img}`}
                        alt="Product"
                        className="max-w-full max-h-full object-contain transition-transform duration-500 group-hover:scale-110"
                        whileHover={{ rotate: [0, -2, 2, 0] }}
                        transition={{ duration: 0.5 }}
                      />

                      {/* Hover Overlay */}
                      <motion.div
                        className="absolute inset-0 bg-gradient-to-t from-primary/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"
                        initial={{ scale: 1.2 }}
                        whileHover={{ scale: 1 }}
                      />

                      {/* View Icon */}
                      <motion.div
                        className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300"
                        initial={{ scale: 0 }}
                        whileHover={{ scale: 1 }}
                        transition={{ delay: 0.1 }}
                      >
                        <div className="bg-white/90 backdrop-blur-sm p-3 rounded-full shadow-xl">
                          <svg
                            className="w-6 h-6 text-primary"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                            />
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
                            />
                          </svg>
                        </div>
                      </motion.div>
                    </div>
                  </div>

                  {/* Product Name */}
                  <div className="p-3 text-center">
                    <h3 className="text-sm font-medium text-gray-800 truncate">
                      {formatProductName(img)}
                    </h3>
                  </div>
                </motion.div>
              ))}

            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {/* Enhanced Modal Viewer */}
      <AnimatePresence>
        {showModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4"
            onClick={closeModal}
          >
            <motion.div
              initial={{ scale: 0.8, opacity: 0, rotateX: 20 }}
              animate={{ scale: 1, opacity: 1, rotateX: 0 }}
              exit={{ scale: 0.8, opacity: 0, rotateX: 20 }}
              transition={{ type: 'spring', damping: 25, stiffness: 300 }}
              className="relative bg-white rounded-3xl shadow-2xl w-full max-w-4xl border border-gray-200 flex flex-col overflow-hidden"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Header */}
              <div className="bg-gradient-to-r from-primary to-primary/90 p-6 flex items-center justify-between">
                <div className="text-white">
                  <h3 className="text-xl font-bold">Product Gallery</h3>
                  <p className="text-primary-light text-sm opacity-90">
                    {selectedIndex + 1} of {productImages[activeTab].length}
                  </p>
                </div>

                <motion.button
                  whileHover={{ scale: 1.1, rotate: 90 }}
                  whileTap={{ scale: 0.9 }}
                  onClick={closeModal}
                  className="bg-white/20 backdrop-blur-sm p-3 rounded-full text-white hover:bg-white/30 transition-colors"
                >
                  <IoClose size={24} />
                </motion.button>
              </div>

              {/* Image Container */}
              <div className="relative bg-gradient-to-br from-gray-50 to-gray-100 flex-1 flex items-center justify-center p-6">
                <motion.img
                  key={selectedIndex}
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ duration: 0.3 }}
                  src={`/${activeTab}/${productImages[activeTab][selectedIndex]}`}
                  alt="Product"
                  className="w-full max-h-[65vh] object-contain rounded-2xl bg-white p-6"
                />

                {/* Image Counter */}
                <div className="absolute top-4 right-4 bg-black/50 backdrop-blur-sm text-white px-3 py-1 rounded-full text-sm font-medium">
                  {selectedIndex + 1} / {productImages[activeTab].length}
                </div>

                {/* Prev Button */}
                <motion.button
                  whileHover={{ scale: 1.2, x: -2 }}
                  whileTap={{ scale: 0.9 }}
                  onClick={prevImage}
                  className="absolute left-4 top-1/2 -translate-y-1/2 bg-black/40 hover:bg-black/60 p-3 rounded-full text-white shadow-lg"
                >
                  <IoChevronBack size={28} />
                </motion.button>

                {/* Next Button */}
                <motion.button
                  whileHover={{ scale: 1.2, x: 2 }}
                  whileTap={{ scale: 0.9 }}
                  onClick={nextImage}
                  className="absolute right-4 top-1/2 -translate-y-1/2 bg-black/40 hover:bg-black/60 p-3 rounded-full text-white shadow-lg"
                >
                  <IoChevronForward size={28} />
                </motion.button>
              </div>

              {/* Product Name (Below Image) */}
              <div className="px-6 pt-3 text-center">
                <h2 className="text-lg font-semibold text-gray-800 tracking-wide">
                  {formatProductName(productImages[activeTab][selectedIndex])}
                </h2>
              </div>

              {/* Dots Indicator */}
              {/* <div className="p-4 bg-gray-50 flex justify-center items-center">
                <div className="flex gap-2">
                  {productImages[activeTab].map((_, index) => (
                    <motion.button
                      key={index}
                      whileHover={{ scale: 1.2 }}
                      onClick={() => setSelectedIndex(index)}
                      className={`w-3 h-3 rounded-full transition-colors ${index === selectedIndex ? 'bg-primary' : 'bg-gray-300'
                        }`}
                    />
                  ))}
                </div>
              </div> */}
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default ProductList;